/**
 * 统一权限判定 —— 与后端 core/permissions.py 语义对齐
 *
 * 为什么要这个模块
 * ----------------
 * 之前 web/h5 两端判断「你是不是体育教师」用的是**自由文本匹配**：
 *
 *     user.teacher_assignments?.some(a =>
 *       a.teaching_group === '体育组' || a.teaching_group === '体育教研组' ||
 *       a.teaching_group?.includes('体育'))
 *
 * 这段在 sports-web/src/layouts/MainLayout.jsx 里**抄了 4 遍**，
 * sports-h5/src/pages/teacher/SportsHome.jsx 里还有第 5 遍（更粗糙，三重 OR）。
 *
 * 问题在于「教研组」是管理员手填的自由文本 —— 写成「体艺组」「体育与健康」
 * 都会漏判，于是同一个体育教师在不同页面看到的入口数不同。
 * 后端 utils/role_tags.py 早就把这条写成了禁令：
 *     「不要再用 "体育" in teaching_group 这种写法」
 *
 * 现在统一读后端下发的 role_tags（用户信息里已带这个字段），
 * 判据与后端完全一致。
 *
 * 重要提醒
 * --------
 * 前端权限判断**只是 UI 提示，不是安全边界**。
 * 后端已对每个端点独立做权限判定（core/permissions.py），
 * 前端藏入口只是体验优化 —— 改前端 JS 或直接调 API 都不会越权。
 * 反过来，前端若比后端宽松，用户会看到点不动/报 403 的按钮。
 */

/** 岗位标签（值必须与后端 utils/role_tags.py 完全一致） */
export const TAG = {
  HEAD: 'head_teacher',
  PE: 'pe_teacher',
  SUBJECT: 'subject_teacher',
};

/** 账号角色 */
export const ROLE = {
  ADMIN: 'admin',
  STAFF: 'staff',
  TEACHER: 'teacher',
  STUDENT: 'student',
  PARENT: 'parent',
};

/** 标签中文名 */
export const TAG_LABEL = {
  [TAG.HEAD]: '班主任',
  [TAG.PE]: '体育教师',
  [TAG.SUBJECT]: '科任教师',
};

/** 角色中文名 */
export const ROLE_LABEL = {
  [ROLE.ADMIN]: '管理员',
  [ROLE.STAFF]: '教务/行政',
  [ROLE.TEACHER]: '教师',
  [ROLE.STUDENT]: '学生',
  [ROLE.PARENT]: '家长',
};

/**
 * 取角色字符串。
 * 用户对象里 role 可能是字符串，也可能是 { value: 'admin' } 这种枚举对象
 * （后端 schemas.UserResponse 的 role 字段就是后者）。
 */
export function roleOf(user) {
  if (!user) return null;
  const r = user.role;
  if (!r) return null;
  if (typeof r === 'string') return r;
  if (typeof r.value === 'string') return r.value;
  return null;
}

/**
 * 取岗位标签数组。
 * 优先用后端下发的 role_tags（已是跨学年并集）；
 * 没有则回退到从 teacher_assignments 推导（仅用于标签未回填的旧账号）。
 */
export function tagsOf(user) {
  if (!user) return [];

  // 后端已下发的标签（权威）
  const fromServer = user.role_tags;
  if (Array.isArray(fromServer)) {
    return fromServer.filter((t) => Object.values(TAG).includes(t));
  }

  // 回退：后端旧版本没有该字段时，从分配里推导
  // 注意这是**兜底**，不是常规路径 —— 生产库 role_tags 已完成迁移
  const assignments = user.teacher_assignments;
  if (!Array.isArray(assignments)) return [];

  const tags = new Set();
  assignments.forEach((a) => {
    if (a.role_tags && Array.isArray(a.role_tags)) {
      a.role_tags.forEach((t) => tags.add(t));
      return;
    }
    if (a.managed_class_id) tags.add(TAG.HEAD);
    const group = a.teaching_group || '';
    const subject = a.subject || '';
    if (group.includes('体育') || subject.includes('体育')) {
      tags.add(TAG.PE);
    } else if (a.teaching_classes && a.teaching_classes.length) {
      tags.add(TAG.SUBJECT);
    }
  });
  return Array.from(tags);
}

/** 是否具备某个岗位标签 */
export function hasTag(user, tag) {
  return tagsOf(user).includes(tag);
}

/** 是否是管理员 */
export function isAdmin(user) {
  return roleOf(user) === ROLE.ADMIN;
}

/** 是否是教务/行政 */
export function isStaff(user) {
  return roleOf(user) === ROLE.STAFF;
}

/** 是否是教师账号（不含 admin/staff） */
export function isTeacherAccount(user) {
  return roleOf(user) === ROLE.TEACHER;
}

/** 是否是家长或学生（家庭成员） */
export function isPersonal(user) {
  const r = roleOf(user);
  return r === ROLE.PARENT || r === ROLE.STUDENT;
}

/** 是否是特权账号（管理员 + 教务） */
export function isPrivileged(user) {
  const r = roleOf(user);
  return r === ROLE.ADMIN || r === ROLE.STAFF;
}

/** 是否是体育教师 */
export function isPETeacher(user) {
  if (isAdmin(user)) return true;
  return hasTag(user, TAG.PE);
}

/** 是否是班主任 */
export function isHeadTeacher(user) {
  return hasTag(user, TAG.HEAD);
}

/** 是否是科任教师 */
export function isSubjectTeacher(user) {
  return hasTag(user, TAG.SUBJECT);
}

/**
 * 能否管理体测（录入、成绩、免测、导出）
 *
 * 口径与后端 role_tags.can_manage_pe_data 一致：
 * 管理员/教务可管全校，体育教师可管本人班级，
 * **科任教师完全看不到体测**。
 */
export function canManagePhysicalTest(user) {
  if (isPrivileged(user)) return true;
  return hasTag(user, TAG.PE);
}

/** 能否审核免测（与体测管理同口径） */
export function canReviewExemption(user) {
  return canManagePhysicalTest(user);
}

/** 能否管理用户（仅管理员） */
export function canManageUsers(user) {
  return isAdmin(user);
}

/** 能否管理系统配置（仅管理员） */
export function canManageSystem(user) {
  return isAdmin(user);
}

/** 能否管理社团 */
export function canManageClubs(user) {
  if (isAdmin(user)) return true;
  return hasTag(user, TAG.PE);
}

/** 能否排课 */
export function canManageScheduling(user) {
  return isPrivileged(user);
}

/** 一句话身份描述 */
export function displayRole(user) {
  const r = roleOf(user);
  if (!r) return '未登录';
  if (r === ROLE.ADMIN) return '管理员';
  if (r === ROLE.STAFF) return '教务/行政';
  if (isPersonal(user)) return '家长/学生';

  const tags = tagsOf(user);
  const label = tags.map((t) => TAG_LABEL[t] || t).join('、');
  return label || '教师（未分配岗位）';
}

/** 完整权限矩阵（供管理界面展示） */
export const PERMISSION_MATRIX = {
  admin: '全部权限',
  staff: '全校只读 + 排课管理',
  teacher: '按岗位标签：班主任管班、体育教师管体测、科任教师看常规数据',
  student: '本人/绑定子女只读',
  parent: '本人/绑定子女只读',
};
